import { createMongoAbility, type MongoAbility, AbilityBuilder } from '@casl/ability';
import { useProfileStore } from '@/stores/profile';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import type { Action, Subject } from './interfaces';

function defineAbilitiesFor(role: string | null): MongoAbility {
  const { can, cannot, rules } = new AbilityBuilder(createMongoAbility);

  switch (role) {
    case 'admin':
      can('manage', 'all');
      cannot('manage', 'Departments');
      cannot('manage', 'Teams');
      cannot('manage', 'Feedbacks');
      cannot('manage', 'CompanyInsights');
      cannot('manage', 'Pricing');
      cannot('read', 'People');
      cannot('read', 'Panel');
      cannot('edit', 'Users');
      cannot('manage', 'TeamAnalytics');
      cannot('read', 'AnalyticsNavItem');
      cannot('manage', 'PsychoPatients');
      cannot('manage', 'PsychologyChats');
      cannot('read', 'Info');
      cannot('manage', 'Sessions');
      cannot('manage', 'Psychologists');
      cannot('manage', 'PsychoCompanyAnalytics');
      cannot('manage', 'PsychoCompany');
      cannot('manage', 'Settings');
      cannot('manage', 'PsychologistAnalytics');
      cannot('manage', 'Other');
      cannot('manage', 'Integrations');
      can('manage', 'Articles');
      can('manage', 'RemoteConfig');
      can('manage', 'Achievements');

      break;

    case 'company-manager':
      can('manage', 'Analytics');
      can('manage', 'TeamAnalytics');
      can('manage', 'Profile');
      can('manage', 'Departments');
      can('manage', 'Team');
      can('manage', 'Teams');
      can('manage', 'Task');
      can('manage', 'Users');
      can('manage', 'Feedbacks');
      can('manage', 'Department');
      can('manage', 'Pricing');
      can('manage', 'OneToOne');
      can('read', 'Dashboard');
      can('read', 'Public');
      can('read', 'People');
      can('read', 'Panel');
      can('read', 'AnalyticsNavItem');
      can('read', 'CompanyInfo');
      can('manage', 'Other');
      can('manage', 'Integrations');
      cannot('manage', 'CompanyInsights');
      cannot('manage', 'User');
      cannot('manage', 'Companies');
      cannot('manage', 'ConsultantRequests');
      cannot('manage', 'ConsultantRequest');
      cannot('manage', 'Insights');
      cannot('manage', 'CompanyTeams');
      cannot('manage', 'Journey');
      cannot('manage', 'MentalStateChats');
      cannot('manage', 'PsychologyChats');

      break;

    case 'psychologist':
      can('manage', 'Sessions');
      can('read', 'Public');
      can('manage', 'Profile');
      can('read', 'Info');
      can('manage', 'PsychoPatients');
      can('manage', 'PsychologyChats');
      can('manage', 'PsychologistAnalytics');
      break;

    case 'psycho_manager':
      can('read', 'Panel');
      can('read', 'Settings');
      can('read', 'Public');
      can('manage', 'Profile');
      can('manage', 'Psychologists');
      can('manage', 'PsychoCompanyAnalytics');
      can('manage', 'PsychoCompany');
      break;

    default:
      cannot('manage', 'all');
      break;
  }

  return createMongoAbility(rules);
}

export function usePermission() {
  const profileStore = useProfileStore();
  const { getRole } = storeToRefs(profileStore);

  const ability = computed(() => defineAbilitiesFor(getRole.value));
  return {
    can: (action: Action, subject: Subject) => ability.value.can(action, subject),
    getRole: () => getRole.value,
  };
}

export const $can = (action: Action, subject: Subject) => usePermission().can(action, subject);
